
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"

interface ImageViewerProps {
  isOpen: boolean
  imageUrl: string
  alt: string
  onClose: () => void
}

const ImageViewer = ({ isOpen, imageUrl, alt, onClose }: ImageViewerProps) => {
  const [isSvg, setIsSvg] = useState(false)

  // Detect if the image is an SVG
  useEffect(() => {
    if (imageUrl) {
      setIsSvg(
        imageUrl.toLowerCase().endsWith(".svg") ||
          imageUrl.toLowerCase().includes(".svg?") ||
          imageUrl.toLowerCase().includes("image/svg"),
      )
    }
  }, [imageUrl])

  // Close on escape key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey)
      // Prevent scrolling of the body when viewer is open
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey)
      // Restore scrolling when viewer is closed
      document.body.style.overflow = ""
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  // Use createPortal to render directly to the document body
  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center"
      onClick={onClose} // Any click anywhere closes the viewer
      style={{ zIndex: 99999 }}
    >
      {/* Close button - still included for accessibility */}
      <button
        className="fixed top-4 right-4 p-3 bg-black/70 rounded-full text-white hover:bg-black/90 transition-colors"
        aria-label="Close"
        style={{ zIndex: 100000 }}
      >
        <X className="w-7 h-7" />
      </button>

      {/* Image - no stopPropagation, clicking it will also close the viewer */}
      <img
        src={imageUrl || "/placeholder.svg"}
        alt={alt || "Full screen image"}
        className={`${isSvg ? "w-[85vw] h-[85vh]" : "max-w-[90vw] max-h-[90vh]"} object-contain`}
        style={{
          width: isSvg ? "100%" : "auto",
          height: isSvg ? "100%" : "auto",
        }}
      />
    </div>,
    document.body,
  )
}

export default ImageViewer

